import React from "react";
import { t } from "ttag";

import { config } from "../../../config";
import { CaptchaLibrary } from "../../../forms/Captcha";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { useHostedApplicationUrl } from "../../../utils/financing";
import { assertNever } from "../../../utils/never";
import { FinancingFullApplyForm } from "../FinancingFullApplyForm";
import { FinancingFullApproval } from "../FinancingFullApproval";
import { FinancingFullDenied } from "../FinancingFullDenied";
import { FinancingFullExistingCardholder } from "../FinancingFullExistingCardholder";
import { FinancingFullNextBackend } from "../FinancingFullNextBackend";
import { FinancingFullPending } from "../FinancingFullPending";
import { FinancingPortal, FinancingRoot } from "../FinancingPortal";
import { FinancingPreQualApproval } from "../FinancingPreQualApproval";
import { FinancingPreQualDenied } from "../FinancingPreQualDenied";
import { FinancingPreQualExistingCardHolder } from "../FinancingPreQualExistingCardHolder";
import { FinancingPreQualForm } from "../FinancingPreQualForm";
import { ModalStateType } from "../constants";
import { ModalState } from "../models";
import { closeModal } from "../reducers";

const ModalContent = (props: { modalState: ModalState }) => {
    switch (props.modalState._tag) {
        case ModalStateType.CLOSED: {
            return null;
        }

        case ModalStateType.PREQUAL_INITIAL:
        case ModalStateType.PREQUAL_SUBMITTING:
        case ModalStateType.PREQUAL_ERROR: {
            return <FinancingPreQualForm />;
        }

        case ModalStateType.PREQUAL_APPROVED: {
            return (
                <FinancingPreQualApproval
                    firstName={props.modalState.data.first_name}
                    backend_type={
                        props.modalState.approvedResponse.backend_type
                    }
                    creditLimit={props.modalState.approvedResponse.credit_limit}
                    apr={props.modalState.approvedResponse.apr}
                    fullDuration={
                        props.modalState.approvedResponse.full_duration
                    }
                />
            );
        }

        case ModalStateType.PREQUAL_DENIED: {
            return <FinancingPreQualDenied />;
        }

        case ModalStateType.PREQUAL_EXISTING_CARDHOLDER: {
            return <FinancingPreQualExistingCardHolder />;
        }

        case ModalStateType.PRESCREEN_APPROVED: {
            return null;
        }

        case ModalStateType.APPLY_INITIAL:
        case ModalStateType.APPLY_SUBMITTING:
        case ModalStateType.APPLY_ERROR: {
            return <FinancingFullApplyForm modalState={props.modalState} />;
        }

        case ModalStateType.APPLY_APPROVED: {
            return <FinancingFullApproval account={props.modalState.account} />;
        }

        case ModalStateType.APPLY_PENDING: {
            return <FinancingFullPending modalState={props.modalState} />;
        }

        case ModalStateType.APPLY_DENIED: {
            return <FinancingFullDenied modalState={props.modalState} />;
        }

        case ModalStateType.APPLY_NEXT_BACKEND: {
            return <FinancingFullNextBackend modalState={props.modalState} />;
        }

        case ModalStateType.APPLY_EXISTING_CARDHOLDER: {
            return <FinancingFullExistingCardholder />;
        }
    }

    throw assertNever(props.modalState);
};

export const FinancingModal = () => {
    const dispatch = useAppDispatch();
    const modalState = useAppSelector((state) => state.financing.modal);
    const hostedUrl = useHostedApplicationUrl();
    const isCSR = useAppSelector((state) => state.common.user?.is_csr ?? false);
    const isOpen =
        modalState._tag !== ModalStateType.CLOSED &&
        modalState._tag !== ModalStateType.PRESCREEN_APPROVED;

    const onOpenChange = (open: boolean) => {
        if (open) {
            return;
        }
        dispatch(closeModal());
    };
    const title = modalState._tag.startsWith("PREQUAL")
        ? t`Pre-Qualification Form`
        : t`Credit Card Application`;

    // In hosted mode the in-site application flow is disabled — financing
    // CTAs link out to Wells Fargo's hosted application instead. CSRs keep the
    // credit application (they submit for customers by phone), so the modal
    // still mounts for them in an APPLY_* state; for everyone else rendering
    // nothing also neutralizes the legacy ?open-credit-app auto-open used by
    // old emails. Prequal states stay inert for CSRs too, matching the trigger,
    // which links pre-qualification out for everyone — so ?resume-prequal
    // remains neutralized in all cases.
    if (hostedUrl && !(isCSR && modalState._tag.startsWith("APPLY_"))) {
        return null;
    }

    return (
        <>
            {config.get("ENABLE_CHECKOUT_CAPTCHA") && <CaptchaLibrary />}
            <FinancingRoot
                modal={true}
                open={isOpen}
                onOpenChange={onOpenChange}
            >
                <FinancingPortal title={title}>
                    <ModalContent modalState={modalState} />
                </FinancingPortal>
            </FinancingRoot>
        </>
    );
};
